/**
 * Feed ranker health — consume get_feed_ranking_health_overview(p_days).
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export interface FeedRankingPoolRow {
    pool: string;
    rows: number;
    users: number;
    avg_penalty: number;
}

export interface FeedRankingDailyRow {
    day: string;
    rows: number;
    users: number;
}

export interface FeedRankingHealthOverview {
    period_days: number;
    summary: {
        rows_logged: number;
        users_with_ranking: number;
        sessions: number;
        avg_rank_position: number;
        avg_composite_penalty: number;
        interacted_served: number;
        unseen_served: number;
        ranker_versions: number;
    };
    by_pool: FeedRankingPoolRow[];
    by_content_type: Array<{ content_type: string; rows: number; users: number }>;
    daily_volume: FeedRankingDailyRow[];
}

export function useFeedRankingAnalytics(days: number = 7) {
    return useQuery<FeedRankingHealthOverview>({
        queryKey: ['admin', 'feed-ranking-health', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_feed_ranking_health_overview', {
                p_days: days,
            });
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] || data) as FeedRankingHealthOverview;
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}
